/**
 * @file JS - Controller - PolygonVectorLayerController.js
 * @author Adam Lemire
 * @since 2020/07/22
 * @version 1.0
 * @summary Dernières modifications : 2021/12/15
 */

import { LayerProperties } from '../../../helpers/Selectors.js';
import { ClassificationType, ColorHexList, ColorPalettes, StyleTypes } from '../../../helpers/Constants.js';
import { getRandomRGBAColor } from '../../../helpers/GlobalFunctions.js';
import '../../../helpers/typedef.js';

/** Controller devant être adapté par VectorLayerAdapter */
export default class PolygonVectorLayerController {
  /*
   * Symbol style
   */

  static createSymbolPreviewDiv () {
    const symbol = '<div class="symbol-preview__content" style="' +
      'width: 60px; ' +
      'height: 30px; ' +
      'border:' + $(LayerProperties.LAYER_STROKE_WIDTH).val() + 'px solid ' + $(LayerProperties.BUTTON.STROKE_COLOR).css('background-color') + '; ' +
      'background:' + $(LayerProperties.FILL.COLOR).css('background-color') + ';' +
      'margin: 0px 0px 0px 20px; ' +
      'position: absolute; ' +
      'top: 50%; ' +
      'MsTransform: translateY(-50%); ' +
      'transform: translateY(-50%); ' +
      '"></div>';

    return symbol;
  }

  static creerDivApercuSymboleAutres () {
    const symbol = '<div class="symbol-preview__content" style="' +
      'width: 60px; ' +
      'height: 30px; ' +
      'border:' + $(LayerProperties.SYMBOLE_AUTRES_CONTOUR_EPAISSEUR).val() + 'px solid ' + $(LayerProperties.SYMBOLE_AUTRES_CONTOUR_COULEUR).css('background-color') + '; ' +
      'background:' + $(LayerProperties.SYMBOLE_AUTRES_COULEUR).css('background-color') + ';' +
      'margin: 0px 0px 0px 20px; ' +
      'position: absolute; ' +
      'top: 50%; ' +
      'MsTransform: translateY(-50%); ' +
      'transform: translateY(-50%); ' +
      '"></div>';

    return symbol;
  }

  static showFeatureStyleProperties () {
    $(LayerProperties.FILL.TITLE_LABEL).show();
    $(LayerProperties.FILL.COLOR_LABEL).show();
    $(LayerProperties.FILL.COLOR).show();
    $(LayerProperties.LAYER_STROKE_COLOR).show();
    $(LayerProperties.LAYER_STROKE_WIDTH).show();
    $(LayerProperties.LABEL_SYMBOL_SIZE).addClass('hidden');
    $(LayerProperties.LAYER_SYMBOL_SIZE).addClass('hidden');
    $(LayerProperties.SYMBOLE_AUTRES_TAILLE).hide();
    $(LayerProperties.SYMBOLE_AUTRES_TAILLE_ETIQUETTE).hide();
  }

  /*
   * Groupes
   */

  /**
   * @param { Map < number, GroupStyle > } symbolStyles
   */
  static createGroupsPropertiesRows (symbolStyles) {
    $('.advanced-preview__row').remove();
    symbolStyles.forEach(function (symbolStyle, index) {
      const row = `
        <tr class="advanced-preview__row" data-group="` + symbolStyle.groupValue + `">
          <td class="advanced-preview__symbol--` + symbolStyle.id + '" style="position:relative; height:30px"><div class="symbol-preview__content symbol-preview__content--polygon symbol-preview__content--' + symbolStyle.id + `">
          </div></td>
          <td class="advanced-preview__group">` + symbolStyle.groupValueLabel + `</td>
          <td class="advanced-preview__label"><input type="text" id="advanced-preview__text--` + symbolStyle.id + '" class="advanced-preview__text" value="' + symbolStyle.groupLabel + `"></td>
          <td class="advanced-preview__counter type-graduated">` + symbolStyle.count + `</td>
        </tr>`;
      // display: block;
      $(LayerProperties.ADVANCED_PREVIEW.CONTAINER).append(row);
      PolygonVectorLayerController.createGroupSymbolPreviewDiv(symbolStyle);
    });
  }

  /**
   * @param { VectorLayer } layer
   * @returns { Map < number, GroupStyle > }
   */
  static getNewSymbolStyles (layer) {
    const symbolStyles = $(LayerProperties.SYMBOL_STYLE_TYPE).val() === StyleTypes.CATEGORIZED
      ? PolygonVectorLayerController.getNewCategorizedSymbolStyles(layer)
      : PolygonVectorLayerController.getNewGraduatedSymbolStyles(layer);
    return symbolStyles;
  }

  /**
   * @param { VectorLayer } layer
   * @returns { Map < number, GroupStyle > }
   */
  static getNewCategorizedSymbolStyles (layer) {
    const groups = layer.obtenirValeursDistinctes($(LayerProperties.GROUP_VALUE_SELECTOR).val());
    const radius = 6;
    const strokeWidth = groups.length < 31 ? 2 : 1;
    const symbolStyles = new Map();

    groups.forEach(function (group, index) {
      const randomColor = index < 30 ? 0 : getRandomRGBAColor();
      const backgroundColor = index < 10
        ? ColorHexList.BRIGHT[index]
        : index < 20
          ? ColorHexList.LIGHT[index - 10]
          : index < 30
            ? ColorHexList.DARK[index - 20]
            : randomColor;
      const strokeColor = index < 10
        ? ColorHexList.DARK[index]
        : index < 20
          ? ColorHexList.BRIGHT[index - 10]
          : index < 30
            ? ColorHexList.LIGHT[index - 20]
            : randomColor;
      const symbolStyle = {
        id: index,
        fillColor: backgroundColor,
        strokeColor: strokeColor,
        strokeWidth: strokeWidth,
        groupLabel: group,
        groupValueLabel: group,
        groupValue: group,
        radius: radius
      };
      symbolStyles.set(group, symbolStyle);
    });
    return symbolStyles;
  }

  /**
   * @param { object } classification
   * @returns { Map < number, GroupStyle > }
   */
  static getNewGraduatedSymbolStyles (classification) {
    const radius = 1;
    const symbolStyles = new Map();
    const precision = $(LayerProperties.PRECISION_SELECTOR).val();
    const counter = classification.groups.counter;

    try {
      if (classification.type === ClassificationType.ATTRIBUTE) {
        const groups = classification.groups.bounds || [0];
        groups.sort(function (a, b) { return a - b; });

        const max = classification.groups.max();

        groups.forEach(function (group, index) {
          const colorPalette = ColorPalettes[$(LayerProperties.COLOR_GRADIENT_SELECTOR).val()];
          const strokeColor = 'black'; // ! a modifier?
          const backgroundColor = colorPalette(1 - group / (max));
          const strokeWidth = 1;

          /** @type { GroupStyle } */
          const symbolStyle = {
            id: index,
            count: counter[index],
            fillColor: backgroundColor,
            strokeColor: strokeColor,
            strokeWidth: strokeWidth,
            groupLabel: group.toFixed(precision),
            groupValueLabel: group.toFixed(precision),
            groupValue: group,
            radius: radius
          };
          symbolStyles.set(group, symbolStyle);
        });
      } else {
        const ranges = classification.groups.ranges;

        ranges.forEach(function (groupLabel, index) {
          const groupNumber = index;
          const colorPalette = ColorPalettes[$(LayerProperties.COLOR_GRADIENT_SELECTOR).val()];
          const strokeColor = 'black';
          console.log(index / (ranges.length - 1));
          const backgroundColor = classification.type === ClassificationType.STD_DEVIATION ? ColorHexList.COLD_HOT[index] : colorPalette(1 - index / (ranges.length - 1)); // (1 - (index + 1) / (ranges.length))
          const strokeWidth = 1;

          /** @type { GroupStyle } */
          const symbolStyle = {
            id: index,
            count: counter[index],
            fillColor: backgroundColor,
            strokeColor: strokeColor,
            strokeWidth: strokeWidth,
            groupLabel: groupLabel,
            groupValueLabel: groupLabel,
            groupValue: groupNumber,
            radius: radius
          };
          symbolStyles.set(groupNumber, symbolStyle);
        });
      }
      return symbolStyles;
    } catch (err) {
      console.log(err.message);
    }
  }

  /**
   * @param { GroupStyle } groupStyle
   */
  static createGroupSymbolPreviewDiv (groupStyle) {
    const div = '.symbol-preview__content--' + groupStyle.id;

    const symbol = '<div style="' +
      'width: 100%; ' +
      'height: 100%; ' +
      'border-width:' + groupStyle.strokeWidth + 'px; ' +
      'border-style: solid; ' +
      'border-color:' + groupStyle.strokeColor + '; ' +
      'background: ' + groupStyle.fillColor + ';' +
      'margin: 0px; ' +
      '"></div>';

    $(div).append(symbol);
  }

  /**
   * @param { GroupStyle } advancedStyleObject
   */
  static updatePropertyEditorValues (advancedStyleObject) {
    $(LayerProperties.FILL.COLOR).css('background-color', advancedStyleObject.fillColor);
    $(LayerProperties.BUTTON.STROKE_COLOR).css('background-color', advancedStyleObject.strokeColor);
    $(LayerProperties.LAYER_STROKE_WIDTH).val(advancedStyleObject.strokeWidth);
    $(LayerProperties.SYMBOL_STYLE_GROUP_LABEL).html(advancedStyleObject.groupLabel);
    $(LayerProperties.SYMBOL_STYLE_GROUP_LABEL).data('group', advancedStyleObject.groupValue);
  }

  /**
   * @param { VectorLayerStyle } vectorLayerStyle
   */
  static updatePropertyEditorGroupSymbol (vectorLayerStyle) {
    if ($(LayerProperties.SYMBOL_STYLE_TYPE).val() !== StyleTypes.SINGLE) {
      const group = $(LayerProperties.SYMBOL_STYLE_GROUP_LABEL).data('group');
      const groupId = vectorLayerStyle.symbolStyles.get(group).id;
      const div = '.symbol-preview__content--' + groupId + ' div';

      $(div).css('border', $(LayerProperties.LAYER_STROKE_WIDTH).val() + 'px solid ' + $(LayerProperties.BUTTON.STROKE_COLOR).css('background-color'));
      $(div).css('background-color', $(LayerProperties.FILL.COLOR).css('background-color'));

      vectorLayerStyle.symbolStyles.get(group).fillColor = $(LayerProperties.FILL.COLOR).css('background-color');
      vectorLayerStyle.symbolStyles.get(group).strokeColor = $(LayerProperties.BUTTON.STROKE_COLOR).css('background-color');
      vectorLayerStyle.symbolStyles.get(group).strokeWidth = $(LayerProperties.LAYER_STROKE_WIDTH).val();
    }
  }

  /*
   * Légende
   */

  /**
   * @param { object } symbolStyle
   * @returns { string }
   */
  static createSingleLegendSymbolDiv (symbolStyle) {
    const symbol = '<div class="legend__single-symbol legend__single-symbol--polygon" style="' +
    'border:' + symbolStyle.strokeWidth + 'px solid ' + symbolStyle.strokeColor + '; ' +
    'background:' + symbolStyle.fillColor + ';' +
    '"></div>';

    return symbol;
  }

  /**
   * @param { Map <number, GroupStyle> } symbolStyles
   * @returns {string}
   */
  static createMultiLegendSymbolsDiv (symbolStyles) {
    let divs = '<table class="legend__multi-styles">';
    symbolStyles.forEach(function (value, key) {
      const div = '<tr class="legend__group-container"><td class="legend__group-symbol legend__group-symbol--polygon"><div style="' +
      'border:' + value.strokeWidth + 'px solid ' + value.strokeColor + '; ' +
      'background:' + value.fillColor + ';' +
      'width: 100%; ' +
      'height: 100%; ' +
      '"></div></div></td><td class="legend__group-label">' + value.groupLabel + '</td></tr>';
      divs += div;
    });
    divs += '</table>';
    return divs;
  }
}
